// Iter 145i — Hot/Cold streak highlight strip for the Accuracy page.
//
// Surfaces the top 3 leagues with the biggest +pp uplift in last 7 days
// vs the 30d baseline, AND the top 3 sliding leagues, so visitors see
// what's working RIGHT NOW (not just the all-time leaderboard).

import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Flame, Snowflake, TrendingUp, TrendingDown } from "lucide-react";
import { countryToFlag } from "../lib/countryFlag";

const StreakCard = ({ entry, kind }) => {
  const isHot = kind === "hot";
  const Icon = isHot ? TrendingUp : TrendingDown;
  const tone = isHot
    ? "border-emerald-500/30 bg-emerald-500/[0.04] hover:border-emerald-500/60"
    : "border-rose-500/30 bg-rose-500/[0.04] hover:border-rose-500/60";
  const driftTone = isHot ? "text-emerald-300" : "text-rose-300";
  const flag = countryToFlag(entry.country) || "🌍";
  const sign = entry.drift_pp > 0 ? "+" : "";

  return (
    <div
      className={[
        "rounded-xl border p-3 transition-colors",
        tone,
      ].join(" ")}
      data-testid={`streak-${kind}-${entry.league_id || entry.league_name}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-base leading-none">{flag}</span>
            <span className="font-bold truncate text-sm">{entry.league_name}</span>
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
            {entry.country || "—"}
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-0.5 font-black font-tabular text-base ${driftTone}`}
          title="Recent 7d vs 30d baseline"
        >
          <Icon className="w-3.5 h-3.5" />
          {sign}{entry.drift_pp}pp
        </span>
      </div>
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground font-tabular">
        <span>
          <span className="opacity-60">Now</span>{" "}
          <strong className={driftTone}>{entry.recent_hit_rate}%</strong>
        </span>
        <span className="opacity-50">·</span>
        <span>
          <span className="opacity-60">Base</span>{" "}
          <strong className="text-foreground">{entry.baseline_hit_rate}%</strong>
        </span>
        <span className="opacity-50">·</span>
        <span>{entry.recent_settled}/{entry.baseline_settled} settled</span>
      </div>
    </div>
  );
};

const AccuracyStreaksStrip = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/accuracy/streaks?baseline_days=30&window_days=7&top_n=3`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div
        className="nd-card p-4 text-xs text-muted-foreground italic"
        data-testid="streaks-loading"
      >
        Loading hot/cold streaks…
      </div>
    );
  }

  const hot = data?.hot || [];
  const cold = data?.cold || [];

  if (!hot.length && !cold.length) {
    return (
      <div
        className="nd-card p-4 text-xs text-muted-foreground italic"
        data-testid="streaks-empty"
      >
        Not enough recent samples to surface streaks yet. Check back tomorrow.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="streaks-strip">
      {/* Hot streaks column */}
      <div className="nd-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-base font-bold flex items-center gap-2">
            <Flame className="w-4 h-4 text-amber-300" />
            Hot right now
          </h3>
          <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-[0.14em]">
            7d vs 30d
          </span>
        </div>
        {hot.length === 0 ? (
          <div
            className="text-[11px] text-muted-foreground italic"
            data-testid="streaks-hot-empty"
          >
            No leagues with significant +pp uplift this week.
          </div>
        ) : (
          <div className="space-y-2">
            {hot.map((entry) => (
              <StreakCard key={`hot-${entry.league_id || entry.league_name}`} entry={entry} kind="hot" />
            ))}
          </div>
        )}
      </div>

      {/* Cold streaks column */}
      <div className="nd-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-base font-bold flex items-center gap-2">
            <Snowflake className="w-4 h-4 text-cyan-300" />
            Cooling down
          </h3>
          <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-[0.14em]">
            7d vs 30d
          </span>
        </div>
        {cold.length === 0 ? (
          <div
            className="text-[11px] text-muted-foreground italic"
            data-testid="streaks-cold-empty"
          >
            No leagues drifting downward — model is holding steady.
          </div>
        ) : (
          <div className="space-y-2">
            {cold.map((entry) => (
              <StreakCard key={`cold-${entry.league_id || entry.league_name}`} entry={entry} kind="cold" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccuracyStreaksStrip;
